import { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { Presets, SplitFlap } from "react-split-flap";

interface SplitFlapTextProps {
  text: string | number;
  /** Delay between physical character-drum steps. */
  timing?: number;
  /** Exact surface behind the flap, for cards with a highlighted background. */
  background?: string;
  /** Start the mechanical animation. Inactive displays use the settled face. */
  animate?: boolean;
  onAnimationComplete?: () => void;
  /** Profile cards already provide their own ordered cadence. */
  bypassConcurrencyLimit?: boolean;
}

const CHESSLA_FLAP_CHARACTERS = Array.from(new Set([...Presets.ALPHANUM, ...Presets.SPECIAL, "/", ":"]));
export const SPLIT_FLAP_TIMING_MS = 34;
export const SPLIT_FLAP_DURATION_MS = 230;

export const getSplitFlapAnimationMs = (text: string | number, timing = SPLIT_FLAP_TIMING_MS) => {
  const uppercaseValue = String(text).toUpperCase();
  const furthestCharacter = Math.max(
    0,
    ...Array.from(uppercaseValue, (character) => {
      const index = CHESSLA_FLAP_CHARACTERS.indexOf(character);
      // This matches react-split-flap's extended-stack fallback.
      return index === -1 ? CHESSLA_FLAP_CHARACTERS.length : index;
    }),
  );
  return (furthestCharacter + 1) * timing + SPLIT_FLAP_DURATION_MS;
};

const MAX_CONCURRENT_FLAP_DISPLAYS = 5;
let activeFlapDisplays = 0;
const pendingFlapDisplays: Array<() => void> = [];

const drainFlapQueue = () => {
  while (activeFlapDisplays < MAX_CONCURRENT_FLAP_DISPLAYS && pendingFlapDisplays.length > 0) {
    pendingFlapDisplays.shift()?.();
  }
};

const scheduleFlapDisplay = (start: (complete: () => void) => void) => {
  let cancelled = false;
  let running = false;

  const complete = () => {
    if (!running) return;
    running = false;
    activeFlapDisplays -= 1;
    drainFlapQueue();
  };
  const run = () => {
    if (cancelled) return;
    running = true;
    activeFlapDisplays += 1;
    start(complete);
  };

  pendingFlapDisplays.push(run);
  drainFlapQueue();

  return () => {
    cancelled = true;
    complete();
  };
};

/** Authentic two-piece Solari display, visually adapted to Chessla.
 * The library owns the four-layer flap mechanics; this wrapper keeps its
 * geometry, colors, typography, and accessibility consistent with the app. */
export const SplitFlapText = ({
  text,
  timing = SPLIT_FLAP_TIMING_MS,
  background,
  animate = true,
  onAnimationComplete,
  bypassConcurrencyLimit = false,
}: SplitFlapTextProps) => {
  const theme = useTheme();
  const value = String(text);
  const [hasActivated, setHasActivated] = useState(false);

  useEffect(() => {
    if (!animate) {
      return;
    }

    let settleTimer: number | undefined;
    const animationMs = getSplitFlapAnimationMs(value, timing);
    const startAnimation = (complete: () => void) => {
      setHasActivated(true);
      // The library advances through the character drum one step per tick.
      settleTimer = window.setTimeout(() => {
        onAnimationComplete?.();
        complete();
      }, animationMs);
    };
    const cancelScheduledDisplay = bypassConcurrencyLimit
      ? (() => {
          startAnimation(() => undefined);
          return () => undefined;
        })()
      : scheduleFlapDisplay(startAnimation);

    return () => {
      if (settleTimer !== undefined) window.clearTimeout(settleTimer);
      cancelScheduledDisplay();
    };
  }, [animate, bypassConcurrencyLimit, onAnimationComplete, timing, value]);
  // Stat cards deliberately disable Paper's elevation overlay, so the flap
  // must use the exact same solid surface rather than a lighter tile.
  const cardSurface = background ?? theme.palette.background.paper;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        verticalAlign: "baseline",
        color: "inherit",
        "& .split-flap-display": {
          display: "inline-flex",
          justifyContent: "flex-start",
          gap: "0.06em",
          fontSize: "inherit !important",
          lineHeight: "inherit",
          perspective: "260px",
        },
        "& .split-flap-digit, & .split-flap-digit[data-mode='alpha']": {
          width: "0.72em !important",
          height: "1em",
          lineHeight: "1em",
          borderRadius: "0 !important",
          background: `${cardSurface} !important`,
          color: "inherit !important",
          fontFamily: "inherit !important",
          fontWeight: "inherit",
        },
        "& .split-flap-part": {
          border: "none",
          borderRadius: "0 !important",
          boxShadow: "none",
          background: `${cardSurface} !important`,
          transformStyle: "preserve-3d",
        },
        "@keyframes chesslaFlapTopA": {
          from: { transform: "rotateX(0deg) translateZ(1px)" },
          "55%, to": { transform: "rotateX(92deg) translateZ(9px)" },
        },
        "@keyframes chesslaFlapTopB": {
          from: { transform: "rotateX(0deg) translateZ(1px)" },
          "55%, to": { transform: "rotateX(92deg) translateZ(9px)" },
        },
        "@keyframes chesslaFlapBottomA": {
          "0%, 48%": { transform: "rotateX(92deg) translateZ(9px)" },
          "78%": { transform: "rotateX(-7deg) translateZ(4px)" },
          to: { transform: "rotateX(0deg) translateZ(1px)" },
        },
        "@keyframes chesslaFlapBottomB": {
          "0%, 48%": { transform: "rotateX(92deg) translateZ(9px)" },
          "78%": { transform: "rotateX(-7deg) translateZ(4px)" },
          to: { transform: "rotateX(0deg) translateZ(1px)" },
        },
        "& .split-flap-part.top.animated.final.phase-a": {
          animationName: "chesslaFlapTopA",
        },
        "& .split-flap-part.top.animated.final.phase-b": {
          animationName: "chesslaFlapTopB",
        },
        "& .split-flap-part.bottom.animated.final.phase-a": {
          animationName: "chesslaFlapBottomA",
        },
        "& .split-flap-part.bottom.animated.final.phase-b": {
          animationName: "chesslaFlapBottomB",
        },
        "& .split-flap-char": {
          height: "200%",
        },
        "& .split-flap-part.top .split-flap-char": {
          top: 0,
        },
        "& .split-flap-part.bottom .split-flap-char": {
          bottom: 0,
        },
        "& .split-flap-hinge": {
          display: "none",
        },
        "& .split-flap-digit::before": {
          display: "none",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .split-flap-part.animated": {
            animationDuration: "1ms !important",
          },
        },
      }}
    >
      {animate ? (
        <SplitFlap
          key={hasActivated ? "animated" : "settled"}
          value={hasActivated ? value : " ".repeat(value.length)}
          length={value.length}
          align="left"
          chars={CHESSLA_FLAP_CHARACTERS}
          animateOnMount={hasActivated}
          timing={timing}
          duration={SPLIT_FLAP_DURATION_MS}
          hinge={false}
          background={cardSurface}
          fontColor="currentColor"
          style={{ fontSize: "inherit" }}
        />
      ) : (
        <Box component="span" className="split-flap-display" aria-label={value}>
          {Array.from(value.toUpperCase()).map((character, index) => (
            <Box
              component="span"
              className="split-flap-digit"
              data-mode="alpha"
              aria-hidden="true"
              key={`${index}:${character}`}
            >
              {character === " " ? "\u00a0" : character}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
